'use client'

import { usePathname } from 'next/navigation'
import { useEffect } from 'react'

/**
 * The three scroll behaviours the whole site shares: the progress bar at the
 * top of the window, the reveal that lifts an element into place the first
 * time it comes into view, and the wave that lights a heading word by word as
 * it rises.
 *
 * Both are one listener for the whole page rather than a hook per component.
 * A page has upwards of eighty revealed elements; eighty IntersectionObservers
 * and eighty pieces of React state to render them would cost far more than the
 * effect is worth, and the elements themselves need no state — once shown they
 * are done.
 */
export function Motion() {
    const pathname = usePathname()

    useEffect(() => {
        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
        const bar = document.querySelector<HTMLElement>('[data-reading-progress]')

        // Hide what is below the fold, then let the observer bring it back.
        // Done here rather than in the markup so a visitor without JavaScript
        // (or with it broken) reads a complete page instead of a blank one.
        const targets = Array.from(document.querySelectorAll<HTMLElement>('[data-reveal]'))

        if (!reduced) {
            for (const target of targets) {
                if (target.getBoundingClientRect().top >= window.innerHeight * 0.92) {
                    target.setAttribute('data-reveal', 'hidden')
                }
            }
        }

        const observer = new IntersectionObserver(
            (entries) => {
                for (const entry of entries) {
                    if (!entry.isIntersecting) {
                        continue
                    }

                    const element = entry.target as HTMLElement
                    // The design staggers a run of siblings by their own
                    // delay, so a grid of four cards arrives as four beats
                    // rather than one.
                    const delay = Number(element.dataset.revealDelay ?? '0')

                    window.setTimeout(() => element.setAttribute('data-reveal', 'shown'), delay)
                    observer.unobserve(element)
                }
            },
            { rootMargin: '0px 0px -8% 0px' },
        )

        for (const target of targets) {
            observer.observe(target)
        }

        // The wave: every word of a marked heading becomes its own span, lit
        // from dormant through the accent to full contrast as the heading
        // rises through the window. Splitting happens once, in the DOM rather
        // than in the markup, so the headings stay server-rendered sentences —
        // and so a screen reader is handed the sentence, not a word list.
        const waves = Array.from(document.querySelectorAll<HTMLElement>('[data-wave]')).map((heading) => ({
            heading,
            words: reduced ? [] : splitIntoWords(heading),
        }))

        let ticking = false

        const onScroll = () => {
            if (ticking) {
                return
            }

            ticking = true

            requestAnimationFrame(() => {
                ticking = false

                if (bar !== null) {
                    const max = document.documentElement.scrollHeight - window.innerHeight
                    const progress = max > 0 ? Math.min(100, (window.scrollY / max) * 100) : 0

                    bar.style.width = `${progress.toFixed(1)}%`
                }

                for (const { heading, words } of waves) {
                    if (words.length === 0) {
                        continue
                    }

                    const top = heading.getBoundingClientRect().top
                    const height = window.innerHeight || 1
                    // The wave runs while the heading travels from just below
                    // the fold to the upper third of the window.
                    const progress = Math.min(1, Math.max(0, (height * 0.94 - top) / (height * 0.62)))
                    const front = progress * (words.length + 3)

                    words.forEach((word, index) => {
                        const distance = front - index

                        word.style.color =
                            distance > 2.1
                                ? 'var(--color-fg)'
                                : distance > 0.55
                                  ? 'var(--color-accent)'
                                  : 'var(--color-dormant)'
                    })
                }
            })
        }

        window.addEventListener('scroll', onScroll, { passive: true })
        onScroll()

        return () => {
            observer.disconnect()
            window.removeEventListener('scroll', onScroll)
        }
        // Re-run per route: the next page has its own elements to reveal, and
        // its own length for the progress bar.
    }, [pathname])

    return null
}

/**
 * Every word of an element as its own span, in document order.
 *
 * Walks the text nodes rather than reading `textContent`, so a heading with an
 * emphasised word inside keeps its emphasis — the span goes around the word,
 * not around the markup.
 */
function splitIntoWords(element: HTMLElement): HTMLElement[] {
    if (element.dataset.wave === 'split') {
        return Array.from(element.querySelectorAll<HTMLElement>('[data-wave-word]'))
    }

    const words: HTMLElement[] = []

    const walk = (node: Node) => {
        for (const child of Array.from(node.childNodes)) {
            if (child.nodeType === Node.TEXT_NODE) {
                const parts = (child.textContent ?? '').split(/(\s+)/)
                const fragment = document.createDocumentFragment()

                for (const part of parts) {
                    if (part.trim() === '') {
                        fragment.appendChild(document.createTextNode(part))

                        continue
                    }

                    const span = document.createElement('span')

                    span.textContent = part
                    span.dataset.waveWord = ''
                    span.style.transition = 'color 520ms cubic-bezier(.16,1,.3,1)'
                    fragment.appendChild(span)
                    words.push(span)
                }

                node.replaceChild(fragment, child)

                continue
            }

            if (child.nodeType === Node.ELEMENT_NODE) {
                walk(child)
            }
        }
    }

    walk(element)
    element.dataset.wave = 'split'

    return words
}
